import { useId } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";
import type { Domain } from "./Tile";
import { formatChartValue } from "./chart-format";

export interface InsightPoint {
  /** Label for the x axis, e.g. "14:00". */
  t: string;
  value: number;
}

interface InsightChartProps {
  data: InsightPoint[];
  domain?: Domain;
  unit?: string;
  /** Compact sparkline (no axes) vs full chart. */
  sparkline?: boolean;
  /**
   * Visual theme. `"default"` is the light/ambient styling used in the design
   * showcase; `"dark"` matches the EnergyFlowChart dark panel (low-opacity
   * gridlines, light-on-dark ticks, dark tooltip) for use inside an EnergyPanel.
   */
  variant?: "default" | "dark";
  className?: string;
}

export const domainVar: Record<Domain, string> = {
  neutral: "var(--muted-foreground)",
  light: "var(--domain-light)",
  climate: "var(--domain-climate)",
  energy: "var(--domain-energy)",
  security: "var(--domain-security)",
  flow: "var(--domain-flow)",
};

// Dark-panel theming, mirroring EnergyFlowChart so the panels read as siblings.
const DARK_TICK = "rgba(255,255,255,0.45)";
const DARK_GRID = "rgba(255,255,255,0.06)";
const DARK_TOOLTIP = {
  background: "rgb(24 24 27)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: "0.75rem",
  color: "rgb(244 244 245)",
  fontSize: 12,
} as const;
const LIGHT_TOOLTIP = {
  background: "var(--popover)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-lg)",
  color: "var(--popover-foreground)",
  fontSize: 12,
} as const;

/** Area chart for Homey Insights time series. */
export function InsightChart({
  data,
  domain = "energy",
  unit = "",
  sparkline = false,
  variant = "default",
  className,
}: InsightChartProps) {
  const uid = useId();
  const color = domainVar[domain];
  const gradientId = `insight-${domain}-${uid}`;
  const isDark = variant === "dark";
  const tickFill = isDark ? DARK_TICK : "var(--muted-foreground)";
  return (
    <div data-slot="insight-chart" className={cn(sparkline ? "h-12" : "h-40", "w-full", className)}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          {!sparkline && (
            <>
              {isDark && <CartesianGrid stroke={DARK_GRID} vertical={false} />}
              <XAxis
                dataKey="t"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 10, fill: tickFill }}
              />
              <YAxis
                width={32}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 10, fill: tickFill }}
              />
              <Tooltip
                formatter={(v) => [formatChartValue(v, unit), ""]}
                contentStyle={isDark ? DARK_TOOLTIP : LIGHT_TOOLTIP}
                labelStyle={isDark ? { color: "rgba(255,255,255,0.6)" } : undefined}
              />
            </>
          )}
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            isAnimationActive
            animationDuration={400}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
